import { motion } from "framer-motion";
import { MapPin, Camera } from "lucide-react";
import { useLanguage } from "../i18n/translations";

const PHOTOS = [
  {
    src: "https://images.unsplash.com/photo-1786515284601-f9ac47d0758a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxzb2xhciUyMHJvb2Z0b3AlMjBwYW5lbHMlMjBob21lJTIwaW5zdGFsbGF0aW9ufGVufDB8fHx8MTc5MTgxMjMwOHww&ixlib=rb-4.1.0&q=85",
    cap: "3 kW Rooftop Install",
    place: "Sector 62, Neda",
    tall: true,
  },
  {
    src: "https://images.unsplash.com/photo-1771479755134-9c1e3143c110?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxzb2xhciUyMHJvb2Z0b3AlMjBwYW5lbHMlMjBob21lJTIwaW5zdGFsbGF0aW9ufGVufDB8fHx8MTc5MTgxMjMwOHww&ixlib=rb-4.1.0&q=85",
    cap: "5 kW Home System",
    place: "Greater Neda West",
  },
  {
    src: "https://images.unsplash.com/photo-1780922577018-f49a4223e2d3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHxzb2xhciUyMHBhbmVsJTIwdGVjaG5pY2lhbiUyMGluc3RhbGxhdGlvbiUyMHNvbGFyJTIwZmFybXxlbnwwfHx8fDE3OTE4MTIzMTV8MA&ixlib=rb-4.1.0&q=85",
    cap: "Commercial Array",
    place: "Ghaziabad",
  },
  {
    src: "https://images.unsplash.com/photo-1780922576891-de081d701d71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxzb2xhciUyMHBhbmVsJTIwdGVjaG5pY2lhbiUyMGluc3RhbGxhdGlvbiUyMHNvbGFyJTIwZmFybXxlbnwwfHx8fDE3OTE4MTIzMTV8MA&ixlib=rb-4.1.0&q=85",
    cap: "Industrial Installation",
    place: "Neda Phase 2",
    tall: true,
  },
  {
    src: "https://images.unsplash.com/photo-1466611653911-95081537e5b7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHw0fHx3aW5kJTIwdHVyYmluZXMlMjBncmVlbiUyMGVuZXJneSUyMGZpZWxkfGVufDB8fHx8MTc5MTgxMjMwOHww&ixlib=rb-4.1.0&q=85",
    cap: "Hybrid Wind-Solar",
    place: "Bulandshahr",
  },
  {
    src: "https://images.unsplash.com/photo-1452179535021-368bb0edc3a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHx3aW5kJTIwdHVyYmluZXMlMjBncmVlbiUyMGVuZXJneSUyMGZpZWxkfGVufDB8fHx8MTc5MTgxMjMwOHww&ixlib=rb-4.1.0&q=85",
    cap: "Agri Solar Pump",
    place: "Dadri",
  },
];

export const Gallery = () => {
  const { t } = useLanguage();

  return (
    <section id="gallery" className="py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.65 }}
          className="mb-12 flex flex-wrap items-end justify-between gap-6"
        >
          <div className="max-w-2xl">
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-eco-bright">{t.gallery.eyebrow}</p>
            <h2 className="font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl lg:text-4xl">{t.gallery.title}</h2>
            <p className="mt-4 text-slate-400">{t.gallery.desc}</p>
          </div>
          <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-navy-900 px-4 py-2 text-xs font-medium text-slate-400" data-testid="gallery-sample-note">
            <Camera size={13} className="text-solar-yellow" /> {t.gallery.note}
          </span>
        </motion.div>

        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="project-gallery">
          {PHOTOS.map((p, i) => (
            <motion.figure
              key={p.cap}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: (i % 4) * 0.08 }}
              className={`group relative overflow-hidden rounded-2xl border border-white/10 ${p.tall ? "row-span-2 h-full min-h-[300px] lg:min-h-[420px]" : "h-44 sm:h-52"}`}
              data-testid={`gallery-item-${i + 1}`}
            >
              <img
                src={p.src}
                alt={`${p.cap} — ${p.place}`}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-950/90 via-navy-950/20 to-transparent opacity-80 transition-opacity duration-300 group-hover:opacity-100" />
              <figcaption className="absolute inset-x-0 bottom-0 p-4">
                <div className="font-display text-sm font-bold text-white">{p.cap}</div>
                <div className="mt-0.5 flex items-center gap-1 text-xs text-slate-300">
                  <MapPin size={11} className="text-solar-yellow" /> {p.place}
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
};
